import { makeStyles } from '@material-ui/styles'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import { listImages } from 'openstack/components/images/actions'
import { imagesSelector } from 'openstack/components/images/selectors'
import { humanReadableSize } from 'openstack/helpers'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { durationBetweenDates } from 'utils/misc'
import { listVolumeSnapshots } from '../snapshots/actions'
import { volumeSnapshotsSelector } from '../snapshots/selectors'
import VolumeTypesPicklist from '../volume-types/VolumeTypesPicklist'
import { bootableVolume, createVolume, listVolumes } from './actions'
import { volumesByIdSelector, volumesSelector } from './selectors'
import UnitPicklist from './UnitPicklist'
import VolumeSourceCards from './VolumeSourceCards'

const useStyles = makeStyles<Theme>((theme) => ({
  withUnits: {
    display: 'flex',
    gap: 8,
    alignItems: 'baseline',
  },
  unitsDropdown: {
    position: 'relative',
    bottom: 1,
  },
}))

interface Props {
  addRoute: Route
}

const imageColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'disk_format',
    label: 'Disk Format',
  },
  {
    id: 'virtual_size',
    label: 'Virtual Disk Size',
    render: humanReadableSize,
  },
  {
    id: 'created_at',
    label: 'Age',
    render: (value) => durationBetweenDates({ labels: ['d'] })(value),
  },
]

const volumeColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'status',
    label: 'Status',
  },
  {
    id: 'description',
    label: 'Description',
  },
  {
    id: 'size',
    label: 'Size',
    render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
  },
  {
    id: 'bootable',
    label: 'Bootable',
  },
]

const TableHeaderTooltip = ({ message }) => {
  return <div>{message}</div>
}

const getSnapshotColumns = ({ volumesById }) => [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'status',
    label: 'Status',
  },
  {
    id: 'description',
    label: 'Description',
  },
  {
    id: 'size',
    label: 'Size',
    render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
  },
  {
    id: 'volumeType',
    label: (
      <TitleWithInfoIcon
        titleText="Volume Type"
        tooltipMsg={
          <TableHeaderTooltip message="This information is inherited from the source volume" />
        }
      />
    ),
    render: (_, item) => {
      const volume = volumesById?.[item?.volume_id]
      return volume?.volume_type
    },
  },
  {
    id: 'bootable',
    label: (
      <TitleWithInfoIcon
        titleText="Bootable"
        tooltipMsg={
          <TableHeaderTooltip message="This information is inherited from the source volume" />
        }
      />
    ),
    render: (_, item) => {
      const volume = volumesById?.[item?.volume_id]
      return volume?.bootable
    },
  },
]

export default function CreateVolumeModal({ addRoute }: Props) {
  const { history, location } = useReactRouter()
  const searchParams = new URLSearchParams(location.search)
  const snapshotId = searchParams.get('snapshotId')
  const [volumeLimitError, setVolumeLimitError] = useState(null)
  const classes = useStyles()
  const defaultParams = {
    name: '',
    sourceImage: null,
    sourceVolume: null,
    sourceSnapshot: null,
    volumeSource: snapshotId ? 'snapshot' : 'none',
    description: '',
    size: null,
    unit: 'GiB',
    volumeType: '',
    bootable: false,
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { loading: loadingImages } = useListAction(listImages, {
    params: {},
  })
  const images = useSelectorWithParams(imagesSelector, {})
  const activeImages = useMemo(() => {
    return images.filter((image) => image.status === 'active')
  }, [images])

  const { loading: loadingVolumes } = useListAction(listVolumes, {
    params: {},
  })
  const volumes = useSelectorWithParams(volumesSelector, {})
  const volumesById = useSelector(volumesByIdSelector)

  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots, {
    params: {},
  })
  const volumeSnapshots = useSelectorWithParams(volumeSnapshotsSelector, {})

  const { update, updating, error, reset } = useUpdateAction(createVolume)

  const {
    update: updateBootVolume,
    error: bootVolumeError,
    reset: bootVolumeReset,
  } = useUpdateAction(bootableVolume)

  const submitForm = useCallback(async () => {
    const body = {
      volume: {
        name: params.name,
        description: params.description,
        size: params.unit === 'GiB' ? params.size : params.size * 1024,
        source_volid: params.volumeSource === 'volume' ? params.sourceVolume?.[0]?.id : undefined,
        snapshot_id:
          params.volumeSource === 'snapshot' ? params.sourceSnapshot?.[0]?.id : undefined,
        imageRef: params.volumeSource === 'image' ? params.sourceImage?.[0]?.id : undefined,
        volume_type: params.volumeType ? params.volumeType : undefined,
      },
    }

    const { success, response } = await update({ body })
    const errorBlock =
      response && response['overLimit']
        ? {
            title: 'Limit Exceeded',
            message: response['overLimit']?.message || 'Volume size limit exceeded',
            raw: null,
          }
        : response && response['code'] === 400 && response['message']
        ? {
            title: '',
            message: response.message,
            raw: response,
          }
        : null

    setVolumeLimitError(errorBlock)

    if (response?.id) {
      setVolumeLimitError(null)
      const { success: bootSuccess } = await updateBootVolume({
        id: response.id,
        isBootable: params.bootable,
      })

      if (success && bootSuccess) {
        handleClose()
      }
    }
  }, [params])

  const selectedVolumeSnapshot = useMemo(() => {
    return volumeSnapshots?.filter((snapshot) => snapshot?.id === snapshotId)
  }, [snapshotId, volumeSnapshots])

  useEffect(() => {
    if (snapshotId) {
      setParams({
        ...params,
        sourceSnapshot: selectedVolumeSnapshot,
      })
    }
  }, [snapshotId, selectedVolumeSnapshot])

  const handleClose = () => {
    setParams(defaultParams)
    setVolumeLimitError(null)
    reset()
    bootVolumeReset()
    history.push(routeManager.routes.openstack.volumes.path())
  }

  useEffect(() => {
    if (!params.sourceVolume?.[0]) {
      return
    }
    if (params.sourceVolume?.[0]?.bootable === 'true') {
      updateParams({ bootable: true })
    }
    if (params.sourceVolume?.[0]?.bootable === 'false') {
      updateParams({ bootable: false })
    }
    if (params.sourceVolume?.[0]?.volume_type) {
      updateParams({ volumeType: params.sourceVolume[0].volume_type })
    }
  }, [params.sourceVolume])

  useEffect(() => {
    if (!params.sourceSnapshot?.[0]) {
      return
    }
    const volume = volumesById?.[params.sourceSnapshot?.[0]?.volume_id]
    if (!volume) {
      return
    }
    if (volume?.bootable === 'true') {
      updateParams({ bootable: true })
    }
    if (volume?.bootable === 'false') {
      updateParams({ bootable: false })
    }
    if (volume?.volume_type) {
      updateParams({ volumeType: volume.volume_type })
    }
  }, [params.sourceSnapshot])

  const snapshotColumns = useMemo(() => {
    return getSnapshotColumns({ volumesById })
  }, [volumesById])

  return (
    <ModalForm
      route={addRoute}
      title={`Create New Volume`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating}
      error={volumeLimitError || error || bootVolumeError}
      submitTitle={`Create Volume`}
      maxWidth={1200}
      // loading={loadingVolumeBackends}
    >
      <>
        <ServiceUnhealthyInfo action="Volume Creation" blockStorage />
        <FormFieldSection title="Choose a Source" step={1}>
          <Text variant="body2">
            The volume will be bootable if the source is an image, another bootable volume, or a
            snapshot of a bootable volume.
          </Text>
          <VolumeSourceCards
            onClick={getParamsUpdater('volumeSource')}
            value={params.volumeSource}
          />
          {params.volumeSource === 'image' && (
            <ListTableField
              id="sourceImage"
              data={activeImages}
              loading={loadingImages}
              columns={imageColumns}
              onChange={getParamsUpdater('sourceImage')}
              value={params.sourceImage}
              uniqueIdentifier="id"
              searchTargets={['name']}
              required
            />
          )}
          {params.volumeSource === 'volume' && (
            <ListTableField
              id="sourceVolume"
              data={volumes}
              loading={loadingVolumes}
              columns={volumeColumns}
              onChange={getParamsUpdater('sourceVolume')}
              value={params.sourceVolume}
              uniqueIdentifier="id"
              searchTargets={['name']}
              required
            />
          )}
          {params.volumeSource === 'snapshot' && (
            <ListTableField
              id="sourceSnapshot"
              data={volumeSnapshots}
              loading={loadingVolumeSnapshots}
              columns={snapshotColumns}
              onChange={getParamsUpdater('sourceSnapshot')}
              value={params?.sourceSnapshot}
              uniqueIdentifier="id"
              searchTargets={['name']}
              required
            />
          )}
        </FormFieldSection>
        <FormFieldSection title="Volume Configuration" step={2}>
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            required
          />
          <TextField
            id="description"
            label="Description"
            onChange={getParamsUpdater('description')}
            value={params.description}
          />
          <div className={classes.withUnits}>
            <TextField
              id="size"
              label="Size"
              // @ts-ignore complaint about string vs number
              onChange={getParamsUpdater('size')}
              value={params.size}
              type="number"
              min={1}
              integer
              required
            />
            <UnitPicklist
              name="unit"
              value={params.unit}
              onChange={getParamsUpdater('unit')}
              className={classes.unitsDropdown}
            />
          </div>
          <PicklistField
            DropdownComponent={VolumeTypesPicklist}
            id="volumeType"
            onChange={getParamsUpdater('volumeType')}
            value={params.volumeType}
            label="Volume Type"
            required
          />
          <CheckboxField
            id="bootable"
            label="Make Bootable"
            value={params.bootable}
            onChange={getParamsUpdater('bootable')}
            info="Bootable volumes may be used to create new virtual machines using that volume as its primary disk."
          />
        </FormFieldSection>
        {/* <FormFieldSection title="Metadata" step={3}>
          <div>Metadata goes here</div>
        </FormFieldSection> */}
      </>
    </ModalForm>
  )
}
